/*
 * Copyright (C) 2013-2026 Neckar IT GmbH, Mössingen, Germany
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Linking this library statically or dynamically with other modules is
 * making a combined work based on this library. Thus, the terms and
 * conditions of the GNU General Public License cover the whole combination.
 *
 * As a special exception, the copyright holders of this library give you
 * permission to link this library with independent modules, regardless of
 * the license terms of these independent modules, and to copy and distribute
 * the resulting combined work under terms of your choice, provided that every
 * copy of the combined work is accompanied by a complete copy of the source
 * code of this library.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */
package it.neckar.geometry

import it.neckar.geometry.RightTriangleType.MissingCornerInFirstQuadrant
import it.neckar.geometry.RightTriangleType.MissingCornerInFourthQuadrant
import it.neckar.geometry.RightTriangleType.MissingCornerInSecondQuadrant
import it.neckar.geometry.RightTriangleType.MissingCornerInThirdQuadrant
import it.neckar.open.annotations.Slow
import it.neckar.open.collections.fastForEach
import it.neckar.open.kotlin.lang.pointIsLeftOfLine
import it.neckar.open.unit.number.MayBeNegative

/**
 * This is the enum describing the 4 possible right triangles inside a rectangle
 * Each corner of th rectangle is also a corner of the triangle, apart from the corner marked by this enum
 * The ordering of the possible missing corners is done along the Cartesian coordinate system as pictured below
 *
 *  IV  |  I
 *  ---------
 *  III | II

 */
enum class RightTriangleType {
  MissingCornerInFirstQuadrant,
  MissingCornerInSecondQuadrant,
  MissingCornerInThirdQuadrant,
  MissingCornerInFourthQuadrant,

  ;

  fun format(): String {
    return when (this) {
      MissingCornerInFirstQuadrant -> "Ecke fehlt rechts oben"
      MissingCornerInSecondQuadrant -> "Ecke fehlt rechts unten"
      MissingCornerInThirdQuadrant -> "Ecke fehlt links unten"
      MissingCornerInFourthQuadrant -> "Ecke fehlt links oben"
    }
  }

  /**
   * Returns true if the given point lies on the filled side of the hypotenuse of the triangle that fills the given bounding rectangle.
   *
   * Attention: the point is *not* checked against the bounding rectangle itself. Callers that need a point-in-triangle
   * test check the bounding rectangle first and this method second.
   */
  fun isPointOnFilledSide(
    pointX: Double,
    pointY: Double,
    x: Double,
    y: Double,
    width: @MayBeNegative Double,
    height: @MayBeNegative Double,
  ): Boolean {
    val left = x
    val right = x + width
    val top = y
    val bottom = y + height

    //Top and bottom are switched because the planner defines y=0 as the bottom of the screen, not the top
    return when (this) {
      MissingCornerInFirstQuadrant -> pointIsLeftOfLine(right, top, left, bottom, pointX, pointY)
      MissingCornerInSecondQuadrant -> pointIsLeftOfLine(left, top, right, bottom, pointX, pointY)
      MissingCornerInThirdQuadrant -> pointIsLeftOfLine(left, bottom, right, top, pointX, pointY)
      MissingCornerInFourthQuadrant -> pointIsLeftOfLine(right, bottom, left, top, pointX, pointY)
    }.not()
  }
}

/**
 * Represents a right-angle triangle.
 *
 * The triangle has a size that starts at a given location. The size may be negative.
 * Therefore, the upper left corner of the triangle is:
 * * the location if the size is positive
 * * location - size if the size is negative
 * The triangle is defined by the corner that is missing to form a rectangle
 */
data class RightTriangle(
  override val location: Coordinates,
  @MayBeNegative
  override val size: Size,
  /**
   * Which corner of the bounding rectangle is missing to form this triangle
   */
  val rightTriangleType: RightTriangleType,
) : Shape {
  constructor(
    x: Double,
    y: Double,
    width: @MayBeNegative Double,
    height: @MayBeNegative Double,
    rightTriangleType: RightTriangleType,
  ) : this(Coordinates(x, y), Size(width, height), rightTriangleType)

  @Slow
  override fun vertices(): List<Coordinates> {
    return buildList {
      if (rightTriangleType != MissingCornerInFirstQuadrant) add(topRight())
      if (rightTriangleType != MissingCornerInSecondQuadrant) add(bottomRight())
      if (rightTriangleType != MissingCornerInThirdQuadrant) add(bottomLeft())
      if (rightTriangleType != MissingCornerInFourthQuadrant) add(topLeft())
    }
  }


  /**
   * Moves the rectangle
   */
  override fun move(deltaX: Double, deltaY: Double): RightTriangle {
    return RightTriangle(location.plus(deltaX, deltaY), size, rightTriangleType)
  }

  override fun move(distance: Distance): RightTriangle {
    return move(distance.x, distance.y)
  }

  override fun withX(newX: Double): RightTriangle {
    return RightTriangle(newX, getY(), getWidth(), getHeight(), rightTriangleType)
  }

  override fun withY(newY: Double): RightTriangle {
    return RightTriangle(getX(), newY, getWidth(), getHeight(), rightTriangleType)
  }

  /**
   * Creates a [Rectangle] from this [Rectangle] with width [newWidth]
   */
  override fun withWidth(newWidth: Double): RightTriangle {
    return RightTriangle(getX(), getY(), newWidth, getHeight(), rightTriangleType)
  }

  /**
   * Creates a [Rectangle] from this [Rectangle] with height [newHeight]
   */
  override fun withHeight(newHeight: Double): RightTriangle {
    return RightTriangle(getX(), getY(), getWidth(), newHeight, rightTriangleType)
  }

  /**
   * Returns a new rectangle object with the given location - but the same height/width
   */
  override fun withLocation(location: Coordinates): RightTriangle {
    if (this.location == location) {
      return this
    }

    return RightTriangle(location, size, rightTriangleType)
  }

  /**
   * Returns a new rectangle that has been extended with the given values
   */
  override fun expand(left: Double, top: Double, right: Double, bottom: Double): RightTriangle {
    return RightTriangle(getX() - left, getY() - top, getWidth() + left + right, getHeight() + top + bottom, rightTriangleType)
  }

  /**
   * Returns true if this rectangle overlaps with another rectangle
   */
  override fun overlaps(other: Shape): Boolean {
    return !doesNotOverlap(other)
  }

  /**
   * Checks if a [Shape] currently collides with this [RightTriangle]
   * Does NOT work with concave shapes
   */
  override fun doesNotOverlap(other: Shape): Boolean {
    /**
     * First, check if the other shape is outside the bounding rectangle
     * If yes, no further calculation is required
     */
    if (super.doesNotOverlap(other)) {
      return true
    }

    /**
     * Check for each point of the other shape that it lies "left" of the triangle's hypotenuse
     * We already know that at this point of the algorithm, the other shape is outside the triangle's bounding rectangle
     * This basically means that, if all the other shape's points are on the same side of the hypotenuse that is "outside" of the triangle, they do not overlap
     * In this case, if one point is "inside" the triangle, there is a collision
     */
    other.vertices().fastForEach { rectangleCorner ->
      when (rightTriangleType) {
        /**
         * Calculate with the start and end point for the hypotenuse for this triangle
         * Top and Bottom are switched as the planner defines y=0 as the bottom of the screen, not the top
         */
        MissingCornerInFirstQuadrant -> if (pointIsLeftOfLine(right, top, left, bottom, rectangleCorner.x, rectangleCorner.y)) return false
        MissingCornerInSecondQuadrant -> if (pointIsLeftOfLine(left, top, right, bottom, rectangleCorner.x, rectangleCorner.y)) return false
        MissingCornerInThirdQuadrant -> if (pointIsLeftOfLine(left, bottom, right, top, rectangleCorner.x, rectangleCorner.y)) return false
        MissingCornerInFourthQuadrant -> if (pointIsLeftOfLine(right, bottom, left, top, rectangleCorner.x, rectangleCorner.y)) return false
      }
    }

    return true
  }


  companion object {
    fun withLTRB(left: Double, top: Double, right: Double, bottom: Double, rightTriangleType: RightTriangleType): RightTriangle {
      return RightTriangle(left, top, right - left, bottom - top, rightTriangleType)
    }

    /**
     * Returns a rectangle that has its origin centered with the given width/height
     */
    fun centered(width: Double, height: Double, rightTriangleType: RightTriangleType): RightTriangle {
      return RightTriangle(Coordinates.of(-width / 2.0, -height / 2.0), Size(width, height), rightTriangleType)
    }

    /**
     * Returns a rectangle that has its origin centered with the given width/height
     */
    fun centered(size: Size, rightTriangleType: RightTriangleType): RightTriangle {
      return RightTriangle(Coordinates.of(-size.width / 2.0, -size.height / 2.0), size, rightTriangleType)
    }

    /**
     * Creates a new rectangle with its origin set to the bottom right corner
     */
    fun bottomRight(size: Size, rightTriangleType: RightTriangleType): RightTriangle {
      return RightTriangle(Coordinates(-size.width, -size.height), size, rightTriangleType)
    }

    /**
     * Creates a new rectangle with its origin set to the bottom left corner
     */
    fun bottomLeft(size: Size, rightTriangleType: RightTriangleType): RightTriangle {
      return RightTriangle(Coordinates(0.0, -size.height), size, rightTriangleType)
    }

    /**
     * Creates a new rectangle with its origin set to the top right corner
     */
    fun topRight(size: Size, rightTriangleType: RightTriangleType): RightTriangle {
      return RightTriangle(Coordinates(-size.width, 0.0), size, rightTriangleType)
    }

    /**
     * Creates a new rectangle with its origin set to the top right corner.
     */
    fun topLeft(size: Size, rightTriangleType: RightTriangleType): RightTriangle {
      return RightTriangle(Coordinates.origin, size, rightTriangleType)
    }

    fun centerLeft(width: Double, height: Double, rightTriangleType: RightTriangleType): RightTriangle {
      return RightTriangle(Coordinates.of(0.0, -height / 2.0), Size(width, height), rightTriangleType)
    }
  }
}

fun triangleAreaFromThreePoints(point1X: Double, point1Y: Double, point2X: Double, point2Y: Double, point3X: Double, point3Y: Double): Double {
  //Link to the method used: https://en.wikipedia.org/wiki/Area_of_a_triangle#Using_coordinates
  return rectangleAreaFromThreePoints(point1X, point1Y, point2X, point2Y, point3X, point3Y) * 0.5
}
